import React, { useEffect } from 'react';

import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { StartToEndDateRangePicker } from '~/src/dash/components/calendar/StartToEndDateRangePicker';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { timezonesSelectOptions } from '~/src/dash/globals/timezone';
import {
  Col,
  Cols,
  IndependentSelectPill,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { resetMomentLocale, updateMomentLocale } from '~/src/locales/momentCfg';

interface IProps {
  cypressParentName: string;
  selectedRanges: IStartToEndDateRanges;
  handleApplyDateRanges: (ranges: IStartToEndDateRanges) => void;
  hasError?: boolean;
  defaultTimezone: string;
  handleApplyTimezone: (timezone: Set<number | string>) => void;
  enableTimezoneDropdown?: boolean;
}

export const DateRangeRow = ({
  cypressParentName,
  handleApplyDateRanges,
  hasError = false,
  selectedRanges,
  defaultTimezone,
  handleApplyTimezone,
  enableTimezoneDropdown = false,
}: IProps): React.ReactNode => {
  const { t } = usePublicaTranslation();

  // move this to top wrapper when ready
  updateMomentLocale(t);

  // this resets it back to Sun-Sat on unmount
  useEffect(() => {
    return () => {
      resetMomentLocale();
    };
  }, []);

  return (
    <Cols
      max
      largeHorizontalPadding
      style={{ position: 'relative', alignItems: 'center' }}
      className={cypressParentName}
    >
      <Col style={{ minWidth: '104px' }}>
        <Cols gap='8px' style={{ alignItems: 'center', minHeight: '42px' }}>
          <Col displayFlex>
            <IconV2 name='calendar' small />
          </Col>
          <Col displayFlex>
            <Text small bold tKey='DateRange' style={{ lineHeight: 1 }} />
          </Col>
        </Cols>
      </Col>
      <Col style={{ padding: '5px 8px 5px 0px' }}>
        <StartToEndDateRangePicker
          handleApply={handleApplyDateRanges}
          hasError={hasError}
          selectedRanges={selectedRanges}
        />
      </Col>
      {/* HIDDEN FOR NOW UNTIL V2 */}
      {enableTimezoneDropdown && (
        <Col>
          <IndependentSelectPill
            displayLabelWithSelection
            options={timezonesSelectOptions}
            handleApplySelect={handleApplyTimezone}
            key='timezone'
            selectKey='timezone'
            enableLabelTooltip
            labelTKey='Timezone'
            selectedOptionIDs={new Set([defaultTimezone])}
            selectedLimit={1}
            isSelectionRequired
          />
        </Col>
      )}
    </Cols>
  );
};
